import { useCallback, useEffect, useRef, useState } from "react";
import { getProgramados, getSprintDevs, atribuirTicket, updateTicketStatus, clearCache } from "../api/sprint.api";
import { bufferSync, bufferTransicao } from "../api/buffer.api";
import { getSetupResponsaveis, putTicketResponsavel } from "../api/setup.api";
import TicketDrawer from "../components/TicketDrawer";

const REFRESH_SEC = 60;

const DEVS_FIXOS = ["Rudi Monteiro", "Marcelo Rossi", "Paulo Lima", "Guilherme Martin"];

/* Nomes abreviados que a API pode retornar → nome canônico do DEVS_FIXOS */
const NAME_ALIASES = {
  "Rudi": "Rudi Monteiro",
};

const normalizeName = (nome) => NAME_ALIASES[nome] ?? nome;

/* ── Grupos do kanban ───────────────────────────── */
const GROUPS = [
  {
    label: "QA",
    color: "#a78bfa",
    statuses: ["Novo", "Aguardando Análise","Em Análise", "Aguardando Aprovação", "Ajuste Especificação"],
  },
  {
    label: "Produção",
    color: "#fbbf24",
    statuses: ["Aguardando Distribuição", "Aguardando Desenvolvimento", "Em curso", "Parado", "Retrabalhar"],
  },
  {
    label: "QA Testes",
    color: "#4ade80",
    statuses: ["Resolvido", "Aguardando Testes", "Em Teste", "Aguardando Versão", "Feedback", "Reteste", "Fechado"],
  },
];

const GROUPED_STATUS_SET = new Set(GROUPS.flatMap(g => g.statuses));

/* ── Status color map ────────────────────────────── */
const STATUS_COLOR = {
  /* QA */
  "Novo":                       "#a78bfa",
  "Aguardando Análise":         "#818cf8",
  "Aguardando Aprovação":       "#c084fc",
  "Ajuste Especificação":       "#e879f9",
  /* Produção */
  "Aguardando Distribuição":    "#60a5fa",
  "Aguardando Desenvolvimento": "#38bdf8",
  "Em curso":                   "#fbbf24",
  "Parado":                     "#fb923c",
  "Retrabalhar":                "#f97316",
  /* QA Testes */
  "Resolvido":                  "#4ade80",
  "Aguardando Testes":          "#4ade80",
  "Em Teste":                   "#4ade80",
  "Aguardando Versão":          "#4ade80",
  "Feedback":                   "#4ade80",
  "Reteste":                    "#4ade80",
  "Fechado":                    "#4ade80",
  /* outros */
  "Rejeitado":                  "#f87171",
};
const sc = (s) => STATUS_COLOR[s] ?? "#94a3b8";

/* Ordem derivada dos grupos */
const STATUS_ORDER = GROUPS.flatMap(g => g.statuses);

const CLOSED_SET = new Set([
  "Ajuste Especificação",
  "Resolvido", "Aguardando Testes", "Em Teste", "Aguardando Versão",
  "Feedback", "Reteste", "Fechado",
]);

function sortStatuses(statuses) {
  return [...statuses].sort((a, b) => {
    const ai = STATUS_ORDER.indexOf(a);
    const bi = STATUS_ORDER.indexOf(b);
    return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
  });
}

/* ══════════════════════════════════════════════════
   Page
══════════════════════════════════════════════════ */
export default function Programacao() {
  const [tickets,     setTickets]     = useState([]);
  const [devs,        setDevs]        = useState([]);
  const [dragging,    setDragging]    = useState(null);
  const [dragOver,    setDragOver]    = useState(null);
  const [dragOverCol, setDragOverCol] = useState(null);
  const [modal,       setModal]       = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(false);
  const [statusToast, setStatusToast] = useState(null);
  const [drawer,      setDrawer]      = useState({ open: false, fetchId: 0 });
  const [responsaveis, setResponsaveis] = useState([]);
  const [devsVisible, setDevsVisible] = useState(true);

  const openDrawer = useCallback((cfg) =>
    setDrawer(prev => ({ ...cfg, open: true, fetchId: prev.fetchId + 1 })), []);

  /* filter state: Set of visible statuses, or null = all */
  const [visible,   setVisible]   = useState(null);
  const [countdown, setCountdown] = useState(REFRESH_SEC);
  const loadRef     = useRef(null);
  const isFirstLoad = useRef(true);

  async function load(isRefresh = false) {
    const first = isFirstLoad.current;
    if (first) setLoading(true);
    setError(false);
    if (isRefresh) clearCache();
    try {
      const [t, d] = await Promise.all([getProgramados(), getSprintDevs()]);
      const ticketsNorm = (Array.isArray(t) ? t : []).map(tk => ({
        ...tk,
        desenvolvedor: tk.desenvolvedor ? normalizeName(tk.desenvolvedor) : tk.desenvolvedor,
      }));
      setTickets(ticketsNorm);
      bufferSync(ticketsNorm.map(tk => ({ id_ticket: tk.id, status_atual: tk.status })));

      const apiDevs  = (d?.developers || []).filter(x => x.nome).map(x => ({ ...x, nome: normalizeName(x.nome) }));
      const apiNomes = new Set(apiDevs.map(x => x.nome));
      const devsFaltando = DEVS_FIXOS
        .filter(nome => !apiNomes.has(nome))
        .map(nome => ({ nome, programado: { total: 0, aberto: 0, fechado: 0 }, emergencia: { total: 0, aberto: 0, fechado: 0 } }));
      setDevs([...apiDevs, ...devsFaltando]);
      isFirstLoad.current = false;
    } catch (e) {
      console.error(e);
      if (first) setError(true);
    } finally {
      if (first) setLoading(false);
    }
  }

  function handleRefresh() {
    loadRef.current(true);
    setCountdown(REFRESH_SEC);
  }

  /* keep ref current so the interval callback never goes stale */
  loadRef.current = load;

  useEffect(() => {
    getSetupResponsaveis().then(setResponsaveis).catch(() => {});
  }, []);

  useEffect(() => {
    loadRef.current();
    setCountdown(REFRESH_SEC);

    const id = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          loadRef.current();
          return REFRESH_SEC;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(id);
  }, []);

  /* group tickets by status */
  const byStatus = tickets.reduce((acc, t) => {
    const s = t.status || "Sem Status";
    (acc[s] = acc[s] || []).push(t);
    return acc;
  }, {});

  const allStatuses  = sortStatuses(Object.keys(byStatus));

  /* apply visibility filter */
  const shownStatuses = visible === null
    ? allStatuses
    : allStatuses.filter(s => visible.has(s));

  function toggleStatus(s) {
    setVisible(prev => {
      const base = prev ?? new Set(allStatuses);
      const next = new Set(base);
      next.has(s) ? next.delete(s) : next.add(s);
      return next.size === allStatuses.length ? null : next;
    });
  }

  function showAll()   { setVisible(null); }
  function showOpen()  { setVisible(new Set(allStatuses.filter(s => !CLOSED_SET.has(s)))); }
  function showClosed(){ setVisible(new Set(allStatuses.filter(s =>  CLOSED_SET.has(s)))); }

  const isActive = (s) => visible === null || visible.has(s);

  const horizontalMode = shownStatuses.length === 1;

  async function handleStatusDrop(ticket, newStatus) {
    if (!ticket || ticket.status === newStatus) return;
    setTickets(prev => prev.map(t => t.id === ticket.id ? { ...t, status: newStatus } : t));
    try {
      await updateTicketStatus(ticket.id, newStatus);
      bufferTransicao(ticket.id, ticket.status, newStatus);
      setStatusToast({ ok: true, msg: `#${ticket.id} → ${newStatus}` });
      loadRef.current(true);
      setCountdown(REFRESH_SEC);
    } catch {
      setTickets(prev => prev.map(t => t.id === ticket.id ? { ...t, status: ticket.status } : t));
      setStatusToast({ ok: false, msg: `Erro ao mover #${ticket.id}` });
    }
    setTimeout(() => setStatusToast(null), 2500);
  }

  /* ── Mapa de sobrecarga: calculado a partir dos devs com tickets ── */
  const _comTickets = devs.filter(d =>
    (d.programado?.total || 0) + (d.emergencia?.total || 0) > 0
  );
  const mediaEquipe = _comTickets.length
    ? Math.round(_comTickets.reduce((s, d) =>
        s + (d.programado?.aberto || 0) + (d.emergencia?.aberto || 0), 0
      ) / _comTickets.length)
    : 0;
  const overloadedMap = new Map(
    _comTickets
      .filter(d => ((d.programado?.aberto || 0) + (d.emergencia?.aberto || 0)) > mediaEquipe)
      .map(d => [d.nome, {
        abertos: (d.programado?.aberto || 0) + (d.emergencia?.aberto || 0),
        media: mediaEquipe,
      }])
  );

  const fetchDev = (nome) => () =>
    Promise.resolve(tickets.filter(t => t.desenvolvedor === nome));

  if (loading) return <PageSkeleton />;
  if (error)   return <ErrorState onRetry={load} />;

  return (
    <div style={page}>

      {/* ── Header ── */}
      <header style={header}>
        <div style={headerLeft}>
          <h1 style={h1}>Programação</h1>
          <span style={sprintBadge}>Sprint Atual</span>
          <span style={counterLabel}>{tickets.length} tickets</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button
            onClick={handleRefresh}
            title="Atualizar agora"
            style={refreshBtn}
            onMouseEnter={e => { e.currentTarget.style.background = "rgba(96,165,250,0.18)"; e.currentTarget.style.borderColor = "rgba(96,165,250,0.45)"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "rgba(96,165,250,0.08)"; e.currentTarget.style.borderColor = "rgba(96,165,250,0.2)"; }}
          >↻</button>
          <CountdownRing seconds={countdown} total={REFRESH_SEC} />
        </div>
      </header>

      {/* ── Filter bar ── */}
      <div style={filterBar}>
        <div style={filterLeft}>
          {/* ── Foco rápido: Aguardando Distribuição ── */}
          <FocusBtn
            count={byStatus["Aguardando Distribuição"]?.length || 0}
            active={
              visible?.size === 1 && visible.has("Aguardando Distribuição")
            }
            onClick={() => {
              const key = "Aguardando Distribuição";
              const isSoloActive =
                visible?.size === 1 && visible.has(key);
              setVisible(isSoloActive ? null : new Set([key]));
            }}
          />

          <div style={dividerV} />
          <span style={filterTitle}>Situações:</span>
          <button
            onClick={showAll}
            style={{ ...quickBtn, ...(visible === null ? quickBtnActive : {}) }}
          >
            Todos
          </button>
          <button onClick={showOpen}   style={quickBtn}>Em aberto</button>
          <button onClick={showClosed} style={quickBtn}>Concluídos</button>
          <div style={dividerV} />
          {allStatuses.map(s => {
            const color  = sc(s);
            const active = isActive(s);
            return (
              <button
                key={s}
                onClick={() => toggleStatus(s)}
                title={`${active ? "Ocultar" : "Mostrar"}: ${s}`}
                style={{
                  ...statusChip,
                  color:           active ? color : "#334155",
                  borderColor:     active ? `${color}50` : "rgba(255,255,255,0.06)",
                  background:      active ? `${color}15` : "rgba(255,255,255,0.03)",
                  textDecoration:  active ? "none" : "line-through",
                  opacity:         active ? 1 : 0.5,
                }}
              >
                <span style={{ ...chipDot, background: active ? color : "#334155" }} />
                {s}
                <span style={chipCount}>{byStatus[s].length}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Ticket columns by status ── */}
      <div style={block}>
        <SectionLabel>Arraste um ticket para o desenvolvedor desejado</SectionLabel>
        {shownStatuses.length === 0
          ? <div style={emptyMsg}>Nenhuma situação selecionada</div>
          : <div style={statusRow}>
              {(() => {
                const ungrouped = shownStatuses.filter(s => !GROUPED_STATUS_SET.has(s));
                const renderCol = (status) => (
                  <StatusColumn
                    key={status}
                    status={status}
                    tickets={byStatus[status]}
                    overloadedMap={overloadedMap}
                    isDropTarget={dragOverCol === status}
                    horizontal={horizontalMode}
                    onDragStart={(t) => setDragging(t)}
                    onDragEnd={() => { setDragOver(null); setDragOverCol(null); }}
                    onColumnDragOver={() => setDragOverCol(status)}
                    onColumnDragLeave={() => setDragOverCol(null)}
                    onColumnDrop={() => { handleStatusDrop(dragging, status); setDragOverCol(null); }}
                  />
                );
                return (
                  <>
                    {(() => {
                      const totalGeral = shownStatuses.reduce((s, st) => s + (byStatus[st]?.length || 0), 0);
                      return GROUPS.map(group => {
                        const cols = group.statuses.filter(s => shownStatuses.includes(s));
                        if (cols.length === 0) return null;
                        const groupTotal = cols.reduce((s, st) => s + (byStatus[st]?.length || 0), 0);
                        const pct = totalGeral ? Math.round(groupTotal / totalGeral * 100) : 0;
                        return (
                          <div key={group.label} style={{ ...groupWrapper, ...(horizontalMode ? { flex: 1 } : {}) }}>
                            <div style={{ ...groupHeader, color: group.color, borderColor: `${group.color}25`, background: `${group.color}08` }}>
                              <span style={{ width: 7, height: 7, borderRadius: "50%", background: group.color, boxShadow: `0 0 6px ${group.color}`, flexShrink: 0 }} />
                              {group.label}
                              <span style={{
                                marginLeft: 8,
                                background: `${group.color}20`,
                                border: `1px solid ${group.color}40`,
                                color: group.color,
                                borderRadius: 10,
                                padding: "1px 8px",
                                fontSize: 10,
                                fontWeight: 800,
                                letterSpacing: 0.2,
                              }}>
                                {groupTotal}
                              </span>
                              <span style={{ color: `${group.color}90`, fontSize: 10, fontWeight: 700 }}>
                                {pct}%
                              </span>
                            </div>
                            <div style={groupCols}>
                              {cols.map(renderCol)}
                            </div>
                          </div>
                        );
                      });
                    })()}
                    {ungrouped.length > 0 && (
                      <div style={groupWrapper}>
                        <div style={{ ...groupHeader, color: "#94a3b8", borderColor: "rgba(148,163,184,0.15)", background: "rgba(148,163,184,0.05)" }}>
                          <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#94a3b8", flexShrink: 0 }} />
                          Outros
                        </div>
                        <div style={groupCols}>
                          {ungrouped.map(renderCol)}
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
        }
      </div>

      {/* ── Developer drop zones ── */}
      <div style={devsBlock}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: 1 }}>
            Desenvolvedores
          </div>
          <button
            onClick={() => setDevsVisible(v => !v)}
            title={devsVisible ? "Ocultar desenvolvedores" : "Mostrar desenvolvedores"}
            style={toggleDevsBtn}
            onMouseEnter={e => { e.currentTarget.style.background = "rgba(96,165,250,0.12)"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "rgba(96,165,250,0.05)"; }}
          >
            {devsVisible ? "▲ Ocultar" : "▼ Mostrar"}
          </button>
        </div>
        {devsVisible && <div style={devGrid}>
          {devs.map((dev, i) => {
            const combined = {
              total:  (dev.programado?.total  || 0) + (dev.emergencia?.total  || 0),
              aberto: (dev.programado?.aberto || 0) + (dev.emergencia?.aberto || 0),
              fechado:(dev.programado?.fechado|| 0) + (dev.emergencia?.fechado|| 0),
            };
            const isOver = dragOver === dev.nome;
            return (
              <DevCard
                key={i}
                dev={dev}
                combined={combined}
                isOver={isOver}
                overloadedMap={overloadedMap}
                onClick={() => openDrawer({
                  title: dev.nome,
                  subtitle: "Tickets atribuídos a este desenvolvedor",
                  color: "#60a5fa",
                  fetchTickets: fetchDev(dev.nome),
                })}
                onDragOver={(e) => { e.preventDefault(); setDragOver(dev.nome); }}
                onDragLeave={() => setDragOver(null)}
                onDrop={() => {
                  if (!dragging) return;
                  setModal({ ticket: dragging, dev });
                  setDragging(null);
                  setDragOver(null);
                }}
              />
            );
          })}
        </div>}
      </div>

      <TicketDrawer
        open={drawer.open}
        onClose={() => setDrawer(d => ({ ...d, open: false }))}
        title={drawer.title}
        subtitle={drawer.subtitle}
        color={drawer.color}
        fetchTickets={drawer.fetchTickets}
        fetchId={drawer.fetchId}
      />

      {modal && (
        <AtribuirModal
          ticket={modal.ticket}
          dev={modal.dev}
          mediaEquipe={mediaEquipe}
          responsaveis={responsaveis}
          devAbertos={(() => {
            const d = devs.find(d => d.nome === modal.dev.nome);
            return d ? (d.programado?.aberto || 0) + (d.emergencia?.aberto || 0) : 0;
          })()}
          onClose={() => setModal(null)}
          onConfirm={() => {
            setModal(null);
            loadRef.current(true);
            setCountdown(REFRESH_SEC);
          }}
        />
      )}

      {/* ── Toast de feedback de mudança de status ── */}
      {statusToast && (
        <div style={{
          position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)",
          padding: "10px 20px", borderRadius: 10, zIndex: 9999,
          background: statusToast.ok ? "rgba(74,222,128,0.15)" : "rgba(248,113,113,0.15)",
          border: `1px solid ${statusToast.ok ? "rgba(74,222,128,0.4)" : "rgba(248,113,113,0.4)"}`,
          color: statusToast.ok ? "#4ade80" : "#f87171",
          fontSize: 13, fontWeight: 700,
          boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
          backdropFilter: "blur(12px)",
          pointerEvents: "none",
        }}>
          {statusToast.ok ? "✓" : "✗"} {statusToast.msg}
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════
   StatusColumn
══════════════════════════════════════════════════ */
function StatusColumn({ status, tickets, overloadedMap, isDropTarget, horizontal, onDragStart, onDragEnd, onColumnDragOver, onColumnDragLeave, onColumnDrop }) {
  const color = sc(status);
  const colStyle = horizontal
    ? { ...glass, width: "100%", padding: "12px", display: "flex", flexDirection: "column", gap: 8, maxHeight: "100%", overflow: "hidden", flexShrink: 0,
        border: isDropTarget ? `2px solid ${color}` : "1px solid rgba(96,165,250,0.1)",
        boxShadow: isDropTarget ? `0 0 20px ${color}30, 0 8px 32px rgba(0,0,0,0.4)` : undefined,
        transition: "border 0.15s, box-shadow 0.15s" }
    : { ...statusCol,
        border: isDropTarget ? `2px solid ${color}` : "1px solid rgba(96,165,250,0.1)",
        boxShadow: isDropTarget ? `0 0 20px ${color}30, 0 8px 32px rgba(0,0,0,0.4)` : undefined,
        transition: "border 0.15s, box-shadow 0.15s" };

  const listStyle = horizontal
    ? { display: "flex", flexDirection: "row", gap: 8, overflowX: "auto", overflowY: "hidden", paddingBottom: 8, flex: 1 }
    : cardList;

  return (
    <div
      style={colStyle}
      onDragOver={e => { e.preventDefault(); onColumnDragOver?.(); }}
      onDragLeave={onColumnDragLeave}
      onDrop={e => { e.preventDefault(); onColumnDrop?.(); }}
    >
      <div style={{ ...statusHead, borderBottomColor: `${color}40` }}>
        <span style={{ ...dot, background: color, boxShadow: `0 0 6px ${color}` }} />
        <span style={{ color, fontSize: 11, fontWeight: 700, flex: 1 }}>{status}</span>
        <span style={countPill}>{tickets.length}</span>
      </div>
      {isDropTarget && (
        <div style={{ textAlign: "center", fontSize: 11, color, fontWeight: 700, padding: "6px", borderRadius: 7, background: `${color}12`, border: `1px dashed ${color}50`, margin: "4px 0" }}>
          Soltar aqui
        </div>
      )}
      <div style={listStyle}>
        {tickets.map((t) => (
          <TicketCard key={t.id} ticket={t} overloadedMap={overloadedMap} horizontal={horizontal} onDragStart={onDragStart} onDragEnd={onDragEnd} />
        ))}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════
   TicketCard
══════════════════════════════════════════════════ */
function TicketCard({ ticket, overloadedMap, horizontal, onDragStart, onDragEnd }) {
  const color = sc(ticket.status);
  const [over, setOver] = useState(false);
  const devInfo = ticket.desenvolvedor ? overloadedMap?.get(ticket.desenvolvedor) : null;

  return (
    <div
      draggable
      onDragStart={() => onDragStart(ticket)}
      onDragEnd={onDragEnd}
      onDoubleClick={() => window.open(`https://redmine.lexsis.com.br/issues/${ticket.id}`, "_blank")}
      onMouseEnter={() => setOver(true)}
      onMouseLeave={() => setOver(false)}
      title="Duplo clique para abrir no Redmine"
      style={{ ...tCard, borderColor: over ? `${color}50` : "rgba(96,165,250,0.08)", ...(horizontal ? { width: 220, flexShrink: 0 } : {}) }}
    >
      <div style={tTop}>
        <span style={{ ...tId, color }}>#{ticket.id}</span>
        <div style={{ display: "flex", gap: 4 }}>
          {ticket.programacao_tipo && (
            <span style={{
              ...tGroup,
              color: ticket.programacao_tipo === 1 ? "#22d3ee" : "#a78bfa",
            }}>
              T{ticket.programacao_tipo}
            </span>
          )}
          {ticket.grupo_entrega && (
            <span style={tGroup}>{ticket.grupo_entrega}</span>
          )}
        </div>
      </div>

      {ticket.subject
        ? <p style={tTitle}>{ticket.subject}</p>
        : <p style={{ ...tTitle, color: "#334155", fontStyle: "italic" }}>
            (título disponível após atualizar API)
          </p>
      }

      <div style={tMeta}>
        {ticket.desenvolvedor && (
          <span style={{ ...tDev, color: devInfo ? "#fbbf24" : "#475569" }}>
            {devInfo ? "⚠" : "👤"} {ticket.desenvolvedor}
          </span>
        )}
        {ticket.projeto && <span style={tProj}>📁 {ticket.projeto}</span>}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════
   DevCard
══════════════════════════════════════════════════ */
function DevCard({ dev, combined, isOver, overloadedMap, onClick, onDragOver, onDragLeave, onDrop }) {
  const pct = combined.total ? Math.round((combined.fechado / combined.total) * 100) : 0;
  const pctColor = pct >= 70 ? "#4ade80" : pct >= 40 ? "#fbbf24" : "#f87171";
  const devInfo = overloadedMap?.get(dev.nome);

  return (
    <div
      onClick={onClick}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      style={{ ...devCard, ...(isOver ? devCardOver : {}), cursor: "pointer" }}
    >
      <div style={devAvatar}>{dev.nome.charAt(0).toUpperCase()}</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <div style={{ ...devNameStyle, color: devInfo ? "#fbbf24" : "#e2e8f0" }}>
          {dev.nome}
        </div>
        {devInfo && (
          <div style={overloadBadge}>
            ⚠ {devInfo.abertos} em aberto
          </div>
        )}
      </div>

      <div style={devStats}>
        <DevStat label="Total"    value={combined.total}   color="#94a3b8" />
        <DevStat label="Abertos"  value={combined.aberto}  color="#fbbf24" />
        <DevStat label="Fechados" value={combined.fechado} color="#4ade80" />
      </div>

      <div>
        <div style={miniBarTrack}>
          <div style={{ height: "100%", borderRadius: 3, width: `${pct}%`, background: pctColor, transition: "width 0.5s ease" }} />
        </div>
        <span style={{ color: pctColor, fontSize: 10, fontWeight: 700 }}>{pct}% concluído</span>
      </div>

      {isOver && <div style={dropHint}>⬇ Soltar aqui</div>}
    </div>
  );
}

function DevStat({ label, value, color }) {
  return (
    <div style={{ textAlign: "center" }}>
      <div style={{ color, fontWeight: 800, fontSize: 18 }}>{value}</div>
      <div style={{ color: "#334155", fontSize: 9, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</div>
    </div>
  );
}

/* ══════════════════════════════════════════════════
   AtribuirModal
══════════════════════════════════════════════════ */
const SIZE_OPTS = ["P", "M", "G", "XG"];
const SIZE_LABEL = { P: "Pequeno · 1h", M: "Médio · 9h", G: "Grande · 18h", XG: "Extra Grande · 36h" };

function AtribuirModal({ ticket, dev, mediaEquipe, devAbertos, responsaveis, onClose, onConfirm }) {
  const [saving,          setSaving]          = useState(false);
  const [erro,            setErro]            = useState(null);
  const [estimadoAnalise, setEstimadoAnalise] = useState("");
  const [size,            setSize]            = useState(ticket.size ? String(ticket.size).toUpperCase().trim() : "");
  const [responsavel,     setResponsavel]     = useState(ticket.responsavel || "");
  const color = sc(ticket.status);

  async function handleConfirm() {
    setSaving(true);
    setErro(null);
    try {
      await Promise.all([
        atribuirTicket(ticket.id, dev.nome, estimadoAnalise || null, size || null),
        responsavel !== (ticket.responsavel || "") ? putTicketResponsavel(ticket.id, responsavel) : null,
      ]);
      onConfirm();
    } catch (err) {
      const msg = err?.response?.data?.error || "Erro ao atribuir ticket. Tente novamente.";
      setErro(msg);
      setSaving(false);
    }
  }

  return (
    <div style={overlay} onClick={saving ? undefined : onClose}>
      <div style={modalBox} onClick={(e) => e.stopPropagation()}>
        <div style={modalHeader}>
          <h2 style={modalTitle}>Atribuir Ticket</h2>
          <button onClick={onClose} style={closeBtn} disabled={saving}>✕</button>
        </div>

        <div style={modalCard}>
          <div style={modalRow}>
            <span style={{ ...modalTicketId, color }}>#{ticket.id}</span>
            <span style={{ ...statusBadge, color, borderColor: `${color}35`, background: `${color}12` }}>
              {ticket.status}
            </span>
            {ticket.programacao_tipo && (
              <span style={{ ...statusBadge, color: ticket.programacao_tipo === 1 ? "#22d3ee" : "#a78bfa", borderColor: "transparent", background: "rgba(255,255,255,0.05)" }}>
                Tipo {ticket.programacao_tipo}
              </span>
            )}
          </div>
          {ticket.subject
            ? <p style={modalSubject}>{ticket.subject}</p>
            : <p style={{ ...modalSubject, color: "#475569", fontStyle: "italic" }}>(título indisponível — atualize a API)</p>
          }
          {ticket.projeto && <span style={modalMeta}>📁 {ticket.projeto}</span>}
          <div style={modalRow}>
            <span style={modalMetaLabel}>Atribuído atualmente:</span>
            <span style={modalCurrentDev}>{ticket.desenvolvedor || "— não atribuído"}</span>
          </div>
        </div>

        <div style={arrowWrap}>
          <div style={arrowLine} /><span style={arrowIcon}>↓</span><div style={arrowLine} />
        </div>

        <div style={{ ...modalCard, border: "1px solid rgba(96,165,250,0.25)", background: "rgba(96,165,250,0.06)" }}>
          <span style={modalMetaLabel}>Atribuir para</span>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 4 }}>
            <div style={{ ...devAvatar, width: 36, height: 36, fontSize: 16 }}>
              {dev.nome.charAt(0).toUpperCase()}
            </div>
            <span style={{ color: "#60a5fa", fontWeight: 800, fontSize: 16 }}>{dev.nome}</span>
          </div>
        </div>

        {/* ── Responsável (custom field Redmine) ── */}
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <label style={{ fontSize: 10, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: 0.7 }}>
            Responsável
          </label>
          <select
            value={responsavel}
            onChange={e => setResponsavel(e.target.value)}
            disabled={saving}
            style={{
              padding: "9px 12px", borderRadius: 8,
              border: "1px solid rgba(96,165,250,0.2)",
              background: "rgba(11,22,42,0.7)",
              color: "#e2e8f0", fontSize: 13, outline: "none",
              fontFamily: "Inter, sans-serif", cursor: saving ? "not-allowed" : "pointer",
            }}
          >
            <option value="">— sem responsável —</option>
            {responsaveis.map(nome => (
              <option key={nome} value={nome}>{nome}</option>
            ))}
          </select>
        </div>

        {/* ── Carga do desenvolvedor ── */}
        {mediaEquipe > 0 && (() => {
          const apos = devAbertos + 1;
          const acima = apos > mediaEquipe;
          const muitoAcima = apos > mediaEquipe * 1.5;
          const barMax = Math.max(mediaEquipe * 2, apos + 1);
          const pctAtual = Math.min((devAbertos / barMax) * 100, 100);
          const pctApos  = Math.min((apos / barMax) * 100, 100);
          const barColor = muitoAcima ? "#f87171" : acima ? "#fb923c" : "#4ade80";
          return (
            <div style={{
              padding: "10px 12px", borderRadius: 8,
              background: muitoAcima ? "rgba(248,113,113,0.06)" : acima ? "rgba(251,146,60,0.06)" : "rgba(74,222,128,0.05)",
              border: `1px solid ${muitoAcima ? "rgba(248,113,113,0.25)" : acima ? "rgba(251,146,60,0.25)" : "rgba(74,222,128,0.2)"}`,
              display: "flex", flexDirection: "column", gap: 8,
            }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: 0.7 }}>
                Carga do desenvolvedor
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#94a3b8" }}>
                <span>Abertos atualmente: <strong style={{ color: "#e2e8f0" }}>{devAbertos}</strong></span>
                <span>Média do time: <strong style={{ color: "#e2e8f0" }}>{mediaEquipe}</strong></span>
                <span>Após atribuição: <strong style={{ color: barColor }}>{apos}</strong></span>
              </div>
              <div style={{ position: "relative", height: 6, borderRadius: 4, background: "rgba(255,255,255,0.07)", overflow: "hidden" }}>
                <div style={{ position: "absolute", left: 0, top: 0, height: "100%", width: `${pctAtual}%`, background: "rgba(96,165,250,0.4)", borderRadius: 4, transition: "width 0.3s" }} />
                <div style={{ position: "absolute", left: 0, top: 0, height: "100%", width: `${pctApos}%`, background: barColor, borderRadius: 4, opacity: 0.7, transition: "width 0.3s" }} />
                {/* marcador de média */}
                <div style={{ position: "absolute", top: -2, bottom: -2, width: 2, borderRadius: 1, background: "#60a5fa", left: `${Math.min((mediaEquipe / barMax) * 100, 100)}%` }} />
              </div>
              {acima && (
                <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: muitoAcima ? "#f87171" : "#fb923c", fontWeight: 700 }}>
                  {muitoAcima ? "⛔" : "⚠"} {muitoAcima ? "Acima da capacidade — considere redistribuir" : "Acima da média do time — avalie antes de confirmar"}
                </div>
              )}
            </div>
          );
        })()}

        {/* ── Estimativa de análise ── */}
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <label style={{ fontSize: 10, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: 0.7 }}>
            Estimativa de desenvolvimento (horas)
          </label>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input
              type="number"
              min="0"
              step="0.5"
              placeholder="Ex: 2.5"
              value={estimadoAnalise}
              onChange={e => setEstimadoAnalise(e.target.value)}
              style={{
                flex: 1, padding: "9px 12px", borderRadius: 8,
                border: "1px solid rgba(96,165,250,0.2)",
                background: "rgba(11,22,42,0.7)",
                color: "#e2e8f0", fontSize: 14, outline: "none",
                fontFamily: "Inter, sans-serif",
              }}
              disabled={saving}
            />
            <span style={{ fontSize: 12, color: "#475569", whiteSpace: "nowrap" }}>h</span>
          </div>
          <span style={{ fontSize: 10, color: "#334155" }}>
            Campo E_Estimado_Analise — preenchido pelo responsável pela sprint, antes do desenvolvedor receber o ticket.
          </span>
        </div>

        {/* ── Size ── */}
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <label style={{ fontSize: 10, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: 0.7 }}>
            Size
          </label>
          <div style={{ display: "flex", gap: 6 }}>
            {SIZE_OPTS.map(opt => {
              const active = size === opt;
              return (
                <button
                  key={opt}
                  type="button"
                  disabled={saving}
                  onClick={() => setSize(active ? "" : opt)}
                  title={SIZE_LABEL[opt]}
                  style={{
                    flex: 1,
                    padding: "8px 0",
                    borderRadius: 8,
                    border: active ? "1px solid rgba(96,165,250,0.55)" : "1px solid rgba(96,165,250,0.15)",
                    background: active ? "rgba(96,165,250,0.18)" : "rgba(11,22,42,0.5)",
                    color: active ? "#93c5fd" : "#475569",
                    fontSize: 13,
                    fontWeight: active ? 800 : 500,
                    cursor: saving ? "not-allowed" : "pointer",
                    transition: "all 0.15s",
                    letterSpacing: 0.5,
                  }}
                >
                  {opt}
                </button>
              );
            })}
          </div>
          {size && (
            <span style={{ fontSize: 10, color: "#475569" }}>{SIZE_LABEL[size]}</span>
          )}
        </div>

        {erro && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 12px", borderRadius: 8, background: "rgba(248,113,113,0.08)", border: "1px solid rgba(248,113,113,0.25)" }}>
            <span style={{ fontSize: 14 }}>⚠</span>
            <span style={{ color: "#f87171", fontSize: 12 }}>{erro}</span>
          </div>
        )}

        <div style={modalActions}>
          <button onClick={onClose} style={btnSecondary} disabled={saving}>
            Cancelar
          </button>
          <button
            onClick={handleConfirm}
            style={{ ...btnPrimary, opacity: saving ? 0.7 : 1, cursor: saving ? "not-allowed" : "pointer" }}
            disabled={saving}
          >
            {saving ? "Atribuindo..." : "Confirmar atribuição"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════
   CountdownRing
══════════════════════════════════════════════════ */
function CountdownRing({ seconds, total }) {
  const r      = 11;
  const circ   = 2 * Math.PI * r;
  const filled = circ * (seconds / total);
  const urgent = seconds <= 10;
  const color  = urgent ? "#f87171" : "#60a5fa";

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <svg
        width={30} height={30}
        style={{ transform: "rotate(-90deg)", flexShrink: 0 }}
      >
        {/* track */}
        <circle cx={15} cy={15} r={r} fill="none"
          stroke="rgba(96,165,250,0.1)" strokeWidth={2.5} />
        {/* progress */}
        <circle cx={15} cy={15} r={r} fill="none"
          stroke={color} strokeWidth={2.5}
          strokeDasharray={circ}
          strokeDashoffset={circ - filled}
          strokeLinecap="round"
          style={{ transition: "stroke-dashoffset 0.8s linear, stroke 0.3s" }}
        />
      </svg>
      <div style={{ display: "flex", flexDirection: "column", lineHeight: 1 }}>
        <span style={{ color, fontSize: 14, fontWeight: 800, letterSpacing: -0.5 }}>
          {String(Math.floor(seconds / 60)).padStart(2, "0")}:
          {String(seconds % 60).padStart(2, "0")}
        </span>
        <span style={{ color: "#334155", fontSize: 9, textTransform: "uppercase", letterSpacing: 0.5 }}>
          próx. atualiz.
        </span>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════
   FocusBtn — foco rápido em Aguardando Distribuição
══════════════════════════════════════════════════ */
function FocusBtn({ count, active, onClick }) {
  return (
    <button
      onClick={onClick}
      title="Mostrar apenas tickets aguardando distribuição"
      style={{
        ...focusBtn,
        background: active
          ? "rgba(96,165,250,0.2)"
          : "rgba(96,165,250,0.07)",
        border: active
          ? "1px solid rgba(96,165,250,0.55)"
          : "1px solid rgba(96,165,250,0.2)",
        boxShadow: active
          ? "0 0 14px rgba(96,165,250,0.25)"
          : "none",
        animation: count > 0 && !active ? "pulseBtn 2s ease-in-out infinite" : "none",
      }}
    >
      <span style={focusDot} />
      <span style={{ color: active ? "#93c5fd" : "#60a5fa", fontWeight: 700, fontSize: 12 }}>
        Aguardando Distribuição
      </span>
      <span style={{
        background: count > 0 ? "#60a5fa" : "rgba(96,165,250,0.2)",
        color: count > 0 ? "#fff" : "#475569",
        borderRadius: 12,
        padding: "1px 8px",
        fontSize: 11,
        fontWeight: 800,
        minWidth: 22,
        textAlign: "center",
      }}>
        {count}
      </span>
    </button>
  );
}

/* ══════════════════════════════════════════════════
   Helpers
══════════════════════════════════════════════════ */
function SectionLabel({ children }) {
  return (
    <div style={{ fontSize: 10, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: 1, marginBottom: 10 }}>
      {children}
    </div>
  );
}

function PageSkeleton() {
  return (
    <div style={page}>
      <div style={{ height: 32, width: 220, borderRadius: 10, background: "rgba(255,255,255,0.04)", marginBottom: 20 }} />
      <div style={{ height: 44, borderRadius: 10, background: "rgba(255,255,255,0.03)", marginBottom: 20 }} />
      <div style={{ display: "flex", gap: 12 }}>
        {[0,1,2,3].map(i => <div key={i} style={{ flex: 1, height: 200, borderRadius: 14, background: "rgba(255,255,255,0.04)" }} />)}
      </div>
    </div>
  );
}

function ErrorState({ onRetry }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "60vh", gap: 12 }}>
      <span style={{ fontSize: 36, color: "#fbbf24" }}>⚠</span>
      <p style={{ color: "#94a3b8" }}>Erro ao carregar tickets</p>
      <button onClick={onRetry} style={btnSecondary}>Tentar novamente</button>
    </div>
  );
}

/* ══════════════════════════════════════════════════
   Styles
══════════════════════════════════════════════════ */
const glass = {
  background: "rgba(11,22,42,0.72)",
  backdropFilter: "blur(18px)",
  WebkitBackdropFilter: "blur(18px)",
  border: "1px solid rgba(96,165,250,0.1)",
  borderRadius: 14,
  boxShadow: "0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.04)",
};

const page      = { display: "flex", flexDirection: "column", gap: 12, height: "calc(100vh - 56px)", overflow: "hidden" };
const header    = { display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 };
const headerLeft = { display: "flex", alignItems: "center", gap: 12 };
const h1        = { fontSize: 22, fontWeight: 800, color: "#f1f5f9", letterSpacing: -0.5 };
const sprintBadge = { padding: "3px 12px", borderRadius: 20, background: "rgba(96,165,250,0.12)", border: "1px solid rgba(96,165,250,0.25)", color: "#60a5fa", fontSize: 12, fontWeight: 700 };
const counterLabel = { color: "#475569", fontSize: 12 };

/* filter bar */
const filterBar = {
  ...glass,
  padding: "10px 14px",
  flexShrink: 0,
  borderRadius: 12,
};

const filterLeft = { display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" };
const filterTitle = { color: "#475569", fontSize: 11, fontWeight: 700, marginRight: 2, whiteSpace: "nowrap" };
const dividerV = { width: 1, height: 18, background: "rgba(96,165,250,0.12)", margin: "0 4px" };

const quickBtn = {
  padding: "3px 10px", borderRadius: 8, cursor: "pointer",
  border: "1px solid rgba(96,165,250,0.12)",
  background: "rgba(255,255,255,0.03)",
  color: "#475569", fontSize: 11, fontWeight: 600,
};
const quickBtnActive = {
  background: "rgba(96,165,250,0.12)",
  border: "1px solid rgba(96,165,250,0.3)",
  color: "#60a5fa",
};

const focusBtn = {
  display: "inline-flex",
  alignItems: "center",
  gap: 7,
  padding: "5px 12px",
  borderRadius: 10,
  cursor: "pointer",
  fontSize: 12,
  fontWeight: 600,
  transition: "all 0.2s",
  whiteSpace: "nowrap",
};

const focusDot = {
  width: 7,
  height: 7,
  borderRadius: "50%",
  background: "#60a5fa",
  boxShadow: "0 0 6px #60a5fa",
  flexShrink: 0,
};

const statusChip = {
  display: "inline-flex", alignItems: "center", gap: 5,
  padding: "3px 9px", borderRadius: 20,
  border: "1px solid", cursor: "pointer",
  fontSize: 10, fontWeight: 600, whiteSpace: "nowrap",
  transition: "all 0.15s",
};
const chipDot   = { width: 5, height: 5, borderRadius: "50%", flexShrink: 0 };
const chipCount = { marginLeft: 2, opacity: 0.6, fontSize: 9 };

/* status columns */
const block   = { display: "flex", flexDirection: "column", flex: 1, overflow: "hidden", minHeight: 0 };
const devsBlock = { flexShrink: 0 };
const statusRow = { display: "flex", gap: 10, overflowX: "auto", flex: 1, paddingBottom: 6 };
const emptyMsg  = { color: "#334155", fontSize: 13, alignSelf: "center", padding: 20 };

const statusCol = {
  ...glass,
  minWidth: 210,
  maxWidth: 240,
  padding: "12px",
  display: "flex",
  flexDirection: "column",
  gap: 8,
  maxHeight: "100%",
  flexShrink: 0,
  overflow: "hidden",
};

const statusHead = { display: "flex", alignItems: "center", gap: 6, borderBottom: "1px solid", paddingBottom: 8, flexShrink: 0 };
const dot = { width: 7, height: 7, borderRadius: "50%", flexShrink: 0 };
const countPill = { background: "rgba(255,255,255,0.06)", borderRadius: 10, padding: "1px 7px", fontSize: 10, color: "#475569", fontWeight: 700 };
const cardList = { display: "flex", flexDirection: "column", gap: 6, overflowY: "auto" };

/* ticket card */
const tCard = {
  background: "rgba(255,255,255,0.03)",
  border: "1px solid",
  borderRadius: 8,
  padding: "10px 11px",
  display: "flex",
  flexDirection: "column",
  gap: 4,
  transition: "border-color 0.15s",
  userSelect: "none",
  cursor: "grab",
};
const tTop  = { display: "flex", justifyContent: "space-between", alignItems: "center" };
const tId   = { fontSize: 11, fontWeight: 800 };
const tGroup = { fontSize: 9, color: "#475569", background: "rgba(255,255,255,0.04)", padding: "1px 5px", borderRadius: 4, border: "1px solid rgba(255,255,255,0.06)" };
const tTitle = { fontSize: 11, color: "#94a3b8", lineHeight: 1.4, margin: 0 };
const tMeta = { display: "flex", flexDirection: "column", gap: 2 };
const tDev  = { fontSize: 10, color: "#475569" };
const tProj = { fontSize: 9, color: "#334155" };

/* dev grid */
const devGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(175px, 1fr))", gap: 12 };

const devCard = {
  ...glass,
  padding: "16px",
  display: "flex",
  flexDirection: "column",
  gap: 10,
  transition: "all 0.15s",
  cursor: "default",
};
const devCardOver = {
  border: "2px solid #60a5fa",
  boxShadow: "0 0 24px rgba(96,165,250,0.35), inset 0 1px 0 rgba(255,255,255,0.06)",
  background: "rgba(96,165,250,0.07)",
};
const devAvatar = {
  width: 42, height: 42, borderRadius: 12,
  background: "linear-gradient(135deg, #3b82f6, #06b6d4)",
  display: "flex", alignItems: "center", justifyContent: "center",
  fontWeight: 800, fontSize: 18, color: "#fff",
  boxShadow: "0 4px 12px rgba(6,182,212,0.3)",
};
const devNameStyle = { color: "#e2e8f0", fontWeight: 700, fontSize: 13 };
const devStats = { display: "flex", justifyContent: "space-between" };
const miniBarTrack = { height: 4, background: "rgba(255,255,255,0.06)", borderRadius: 3, overflow: "hidden", marginBottom: 3 };
const dropHint = { textAlign: "center", fontSize: 11, color: "#60a5fa", fontWeight: 700, padding: "5px", borderRadius: 8, background: "rgba(96,165,250,0.1)", border: "1px dashed rgba(96,165,250,0.3)" };

const overloadBadge = {
  display: "inline-flex",
  alignItems: "center",
  padding: "2px 8px",
  borderRadius: 20,
  background: "rgba(251,191,36,0.12)",
  border: "1px solid rgba(251,191,36,0.3)",
  color: "#fbbf24",
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: 0.2,
};

/* kanban groups */
const groupWrapper = {
  display: "flex",
  flexDirection: "column",
  gap: 6,
  flexShrink: 0,
};
const groupHeader = {
  display: "flex",
  alignItems: "center",
  gap: 7,
  padding: "4px 12px",
  borderRadius: 8,
  border: "1px solid",
  fontSize: 10,
  fontWeight: 800,
  textTransform: "uppercase",
  letterSpacing: 1,
  flexShrink: 0,
};
const groupCols = {
  display: "flex",
  flexDirection: "row",
  gap: 10,
  flex: 1,
};

const toggleDevsBtn = {
  padding: "2px 10px", borderRadius: 6, cursor: "pointer",
  border: "1px solid rgba(96,165,250,0.2)",
  background: "rgba(96,165,250,0.05)",
  color: "#60a5fa", fontSize: 10, fontWeight: 700,
  transition: "background 0.15s",
  letterSpacing: 0.3,
};

const refreshBtn = {
  width: 34, height: 34, borderRadius: 8,
  border: "1px solid rgba(96,165,250,0.2)",
  background: "rgba(96,165,250,0.08)",
  color: "#60a5fa", fontSize: 18, cursor: "pointer",
  display: "flex", alignItems: "center", justifyContent: "center",
  transition: "background 0.15s, border-color 0.15s",
  flexShrink: 0, lineHeight: 1,
};

/* modal */
const overlay  = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", backdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 };
const modalBox = { background: "#0b1629", border: "1px solid rgba(96,165,250,0.2)", borderRadius: 18, padding: "26px 28px", width: 460, maxWidth: "92vw", boxShadow: "0 28px 72px rgba(0,0,0,0.8)", display: "flex", flexDirection: "column", gap: 14 };
const modalHeader = { display: "flex", justifyContent: "space-between", alignItems: "center" };
const modalTitle  = { color: "#f1f5f9", fontSize: 16, fontWeight: 800, margin: 0 };
const closeBtn    = { background: "none", border: "none", color: "#475569", fontSize: 18, cursor: "pointer", lineHeight: 1 };
const modalCard   = { background: "rgba(255,255,255,0.03)", border: "1px solid rgba(96,165,250,0.1)", borderRadius: 12, padding: "14px 16px", display: "flex", flexDirection: "column", gap: 8 };
const modalRow    = { display: "flex", alignItems: "center", gap: 10 };
const modalTicketId  = { fontWeight: 800, fontSize: 15 };
const statusBadge    = { padding: "2px 10px", borderRadius: 12, fontSize: 11, fontWeight: 600, border: "1px solid" };
const modalSubject   = { color: "#e2e8f0", fontSize: 14, fontWeight: 500, margin: 0, lineHeight: 1.5 };
const modalMeta      = { color: "#475569", fontSize: 11 };
const modalMetaLabel = { color: "#334155", fontSize: 10, textTransform: "uppercase", letterSpacing: 0.6, fontWeight: 600 };
const modalCurrentDev = { color: "#94a3b8", fontSize: 13, fontWeight: 600 };
const arrowWrap   = { display: "flex", alignItems: "center", gap: 8 };
const arrowLine   = { flex: 1, height: 1, background: "rgba(96,165,250,0.1)" };
const arrowIcon   = { color: "#475569", fontSize: 18 };
const modalActions = { display: "flex", gap: 10, justifyContent: "flex-end", paddingTop: 4 };
const btnSecondary = { padding: "9px 20px", borderRadius: 9, border: "1px solid rgba(255,255,255,0.08)", background: "transparent", color: "#64748b", fontSize: 13, fontWeight: 600, cursor: "pointer" };
const btnPrimary   = { padding: "9px 20px", borderRadius: 9, border: "1px solid rgba(96,165,250,0.4)", background: "rgba(96,165,250,0.15)", color: "#60a5fa", fontSize: 13, fontWeight: 700, cursor: "pointer" };
